import React, { useEffect, useState, useCallback } from 'react';
import { StatusBadge } from '../shared/StatusBadge';
import LoadingSpinner from '../shared/LoadingSpinner';
import { formatDateTime } from '../../utils/dateFormatters';
import { TabContent, DetailPanel, ListDetailLayout, ListPanel, EditActionsBar } from '../../pages/admin/AdminPage.styles';
import { CoatingTypeEnum, getCoatingTypeName } from '../../enums/CoatingTypeEnum';
import { EditableInput } from '../product/EditableFields.styles';
import { useEditMode } from '../../context/EditModeContext';
import { useAuthStore } from '../../context/AuthContext';
import useUpdateCoating from '../../api/useUpdateCoating';
import useGetCoatingManufacturers from '../../api/useGetCoatingManufacturers';

const COATING_TYPE_OPTIONS = [
    { value: CoatingTypeEnum.Unknown, label: getCoatingTypeName(CoatingTypeEnum.Unknown) },
    { value: CoatingTypeEnum.StainlessSteel, label: getCoatingTypeName(CoatingTypeEnum.StainlessSteel) },
    { value: CoatingTypeEnum.Epoxy, label: getCoatingTypeName(CoatingTypeEnum.Epoxy) },
    { value: CoatingTypeEnum.Zink, label: getCoatingTypeName(CoatingTypeEnum.Zink) },
    { value: CoatingTypeEnum.MarineLine, label: getCoatingTypeName(CoatingTypeEnum.MarineLine) },
];

interface CoatingTabProps {
    coatings: any[];
    coatingsLoading: boolean;
    coatingsError: string | null;
    selectedCoating: any;
    setSelectedCoating: (coating: any) => void;
    onRefresh?: () => void;
}

const CoatingTab: React.FC<CoatingTabProps> = ({ coatings, coatingsLoading, coatingsError, selectedCoating, setSelectedCoating, onRefresh }) => {
    const { updateCoating, isLoading: isUpdating } = useUpdateCoating();
    const { isInEditMode, setEditMode } = useEditMode();
    const { loggedInUser } = useAuthStore();
    const { manufacturers, getCoatingManufacturers } = useGetCoatingManufacturers();
    const [formData, setFormData] = useState<any>(null);
    const [updateError, setUpdateError] = useState<string | null>(null);

    const canEdit = loggedInUser !== null;

    // Load coating manufacturers on mount
    useEffect(() => {
        getCoatingManufacturers();
    }, [getCoatingManufacturers]);

    // Initialize form data when coating is selected
    useEffect(() => {
        if (selectedCoating) {
            setFormData(selectedCoating);
            // Don't reset edit mode if this is a new item (id === 0)
            if (selectedCoating.id !== 0) {
                setEditMode(false);
            }
        }
    }, [selectedCoating, setEditMode]);

    // Handle edit mode
    const handleEdit = useCallback(() => {
        setEditMode(true);
        setUpdateError(null);
    }, [setEditMode]);

    // Handle add new coating
    const handleAddNew = useCallback(() => {
        const newCoating = {
            id: 0,
            name: '',
            manufacturer: null,
            manufacturerId: null,
            type: CoatingTypeEnum.Unknown,
            isActive: true,
            lastUpdatedUtc: new Date().toISOString(),
        };
        setSelectedCoating(newCoating);
        setEditMode(true);
        setUpdateError(null);
    }, [setEditMode, setSelectedCoating]);

    // Handle cancel
    const handleCancel = useCallback(() => {
        setFormData(selectedCoating);
        setEditMode(false);
        setUpdateError(null);
    }, [selectedCoating, setEditMode]);

    // Handle save
    const handleSave = useCallback(async () => {
        if (!formData) return;

        try {
            await updateCoating(formData);
            setEditMode(false);
            setUpdateError(null);
            // Refresh the list to show updated data
            onRefresh?.();
        } catch (error: any) {
            setUpdateError(error.message);
        }
    }, [formData, updateCoating, setEditMode, onRefresh]);

    // Handle field changes
    const handleFieldChange = useCallback((field: string, value: any) => {
        setFormData((prev: any) => {
            if (!prev) return prev;
            return {
                ...prev,
                [field]: value,
            };
        });
    }, []);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!coatings.length) return;

            const currentIndex = coatings.findIndex((c) => c.id === selectedCoating?.id);

            switch (event.key) {
                case 'ArrowDown': {
                    event.preventDefault();
                    const nextIndex = currentIndex < coatings.length - 1 ? currentIndex + 1 : 0;
                    setSelectedCoating(coatings[nextIndex]);
                    break;
                }
                case 'ArrowUp': {
                    event.preventDefault();
                    const prevIndex = currentIndex > 0 ? currentIndex - 1 : coatings.length - 1;
                    setSelectedCoating(coatings[prevIndex]);
                    break;
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [coatings, selectedCoating, setSelectedCoating]);
    return (
        <TabContent>
            <ListDetailLayout>
                <ListPanel>
                    {coatingsLoading && <LoadingSpinner text="Loading coatings..." />}

                    {coatingsError && (
                        <div className="error-state">
                            <i className="fas fa-exclamation-triangle"></i> Error loading coatings: {coatingsError}
                        </div>
                    )}

                    {!coatingsLoading && !coatingsError && coatings.length === 0 && (
                        <div className="empty-state">
                            <i className="fas fa-paint-brush"></i>
                            <p>No coatings found</p>
                        </div>
                    )}

                    {!coatingsLoading && coatings.length > 0 && (
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Active</th>
                                        <th>Name</th>
                                        <th>Manufacturer</th>
                                        <th>Type</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {coatings.map((coating) => (
                                        <tr
                                            key={coating.id}
                                            className={`table-row ${!coating.isActive ? 'inactive' : ''} ${
                                                selectedCoating?.id === coating.id ? 'selected' : ''
                                            }`}
                                            onClick={() => setSelectedCoating(coating)}
                                        >
                                            <td>
                                                <StatusBadge variant={coating.isActive ? 'active' : 'inactive'} size="list">
                                                    {coating.isActive ? 'Active' : 'Inactive'}
                                                </StatusBadge>
                                            </td>
                                            <td className="name-cell">{coating.name}</td>
                                            <td className="manufacturer-cell">{coating.manufacturer.name}</td>
                                            <td className="type-cell">{getCoatingTypeName(coating.type)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </ListPanel>

                <DetailPanel>
                    {selectedCoating && formData ? (
                        <>
                            {isInEditMode && (
                                <EditActionsBar>
                                    <button onClick={handleAddNew} className="btn-add-new" title="Add new coating">
                                        <i className="fas fa-plus"></i> New Coating
                                    </button>
                                    <div>
                                        <button onClick={handleSave} className="btn-save" disabled={isUpdating} title="Save changes">
                                            <i className="fas fa-save"></i> {isUpdating ? 'Saving...' : 'Save'}
                                        </button>
                                        <button onClick={handleCancel} className="btn-cancel" disabled={isUpdating} title="Cancel changes">
                                            <i className="fas fa-times"></i> Cancel
                                        </button>
                                    </div>
                                </EditActionsBar>
                            )}
                            <div className="detail-content">
                                <div className="detail-header">
                                    <h3>{formData.name}</h3>
                                    <div>
                                        <div className="header-actions">
                                            <StatusBadge variant={formData.isActive ? 'active' : 'inactive'} size="medium">
                                                {formData.isActive ? 'Active' : 'Inactive'}
                                            </StatusBadge>
                                            {canEdit && !isInEditMode && (
                                                <button onClick={handleEdit} className="edit-btn" title="Edit">
                                                    <i className="fas fa-pencil-alt"></i>
                                                </button>
                                            )}
                                        </div>
                                        {formData.lastUpdatedUtc && (
                                            <div className="last-updated">Last updated: {formatDateTime(formData.lastUpdatedUtc)}</div>
                                        )}
                                    </div>
                                </div>
                                {updateError && (
                                    <div className="error-alert">
                                        <i className="fas fa-exclamation-triangle"></i> {updateError}
                                    </div>
                                )}
                                <div className="detail-body">
                                    <div className="detail-section">
                                        <h4>Basic Information</h4>
                                        <div className="detail-grid">
                                            <div className="detail-item">
                                                <label>Name</label>
                                                {isInEditMode ? (
                                                    <EditableInput
                                                        value={formData.name || ''}
                                                        onChange={(e) => handleFieldChange('name', e.target.value)}
                                                        placeholder="Enter coating name"
                                                    />
                                                ) : (
                                                    <span>{formData.name}</span>
                                                )}
                                            </div>
                                            <div className="detail-item">
                                                <label>Manufacturer</label>
                                                {isInEditMode ? (
                                                    <select
                                                        value={formData.manufacturer?.id || formData.manufacturerId || ''}
                                                        onChange={(e) => {
                                                            const selectedId = parseInt(e.target.value, 10);
                                                            const selectedManufacturer = manufacturers.find((m) => m.id === selectedId);
                                                            handleFieldChange('manufacturer', selectedManufacturer || null);
                                                            handleFieldChange('manufacturerId', selectedId);
                                                        }}
                                                        className="edit-select"
                                                        title="Manufacturer"
                                                    >
                                                        <option value="">-- Select Manufacturer --</option>
                                                        {manufacturers.map((m) => (
                                                            <option key={m.id} value={m.id}>
                                                                {m.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <span>{formData.manufacturer?.name || formData.manufacturerName || 'N/A'}</span>
                                                )}
                                            </div>
                                            <div className="detail-item">
                                                <label>Type</label>
                                                {isInEditMode ? (
                                                    <select
                                                        value={formData.type ?? ''}
                                                        onChange={(e) => handleFieldChange('type', parseInt(e.target.value, 10))}
                                                        className="edit-select"
                                                        title="Coating type"
                                                    >
                                                        {COATING_TYPE_OPTIONS.map((option) => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <span>{getCoatingTypeName(formData.type)}</span>
                                                )}
                                            </div>
                                            <div className="detail-item">
                                                <label>Active</label>
                                                {isInEditMode ? (
                                                    <select
                                                        value={formData.isActive ? 'true' : 'false'}
                                                        onChange={(e) => handleFieldChange('isActive', e.target.value === 'true')}
                                                        className="edit-select"
                                                        title="Active status"
                                                    >
                                                        <option value="true">Yes</option>
                                                        <option value="false">No</option>
                                                    </select>
                                                ) : (
                                                    <span>{formData.isActive ? 'Yes' : 'No'}</span>
                                                )}
                                            </div>
                                            <div className="detail-item">
                                                <label>External Id</label>
                                                <span>{formData.id}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="detail-placeholder">
                            <i className="fas fa-paint-brush"></i>
                            <h3>Select a coating</h3>
                            <p>Choose a coating from the list to view its details</p>
                        </div>
                    )}
                </DetailPanel>
            </ListDetailLayout>
        </TabContent>
    );
};

export default CoatingTab;
